import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw, Layers, X } from 'lucide-react';

const ProductionTemplate = () => {
  const { api, isAdmin, hasRole } = useAuth();
  const [data, setData] = useState([]);
  const [moulds, setMoulds] = useState([]);
  const [parts, setParts] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '', mould_id: '', status: 'ACTIVE', notes: '',
  });
  const [templateParts, setTemplateParts] = useState([]);
  const [templateMaterials, setTemplateMaterials] = useState([]);

  const canEdit = isAdmin() || hasRole(['ADMIN', 'PRODUCTION']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/production-templates');
      setData(res.data);
    } catch {
      toast.error('Failed to fetch production templates');
    } finally {
      setLoading(false);
    }
    try { const r = await api.get('/moulds'); setMoulds(r.data.filter(m => m.is_active)); } catch (e) {}
    try { const r = await api.get('/parts'); setParts(r.data.filter(p => p.is_active)); } catch (e) {}
    try { const r = await api.get('/items'); setItems(r.data.filter(i => i.is_active)); } catch (e) {}
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getMouldLabel = (id) => {
    const m = moulds.find(x => x.id === id);
    return m ? `${m.code} - ${m.name}` : '-';
  };
  const getPartLabel = (id) => {
    const p = parts.find(x => x.id === parseInt(id));
    return p ? `${p.part_no} - ${p.name}` : '-';
  };
  const getItemLabel = (id) => {
    const i = items.find(x => x.id === parseInt(id));
    return i ? `${i.code} - ${i.name}` : '-';
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        mould_id: item.mould_id ? item.mould_id.toString() : '',
        status: item.status || 'ACTIVE',
        notes: item.notes || '',
      });
      setTemplateParts((item.parts || []).map(p => ({ part_id: p.part_id.toString() })));
      setTemplateMaterials((item.materials || []).map(m => ({ item_id: m.item_id.toString() })));
    } else {
      setEditingItem(null);
      setFormData({ name: '', mould_id: '', status: 'ACTIVE', notes: '' });
      setTemplateParts([]);
      setTemplateMaterials([]);
    }
    setIsDialogOpen(true);
  };

  const addPart = () => setTemplateParts(prev => [...prev, { part_id: '' }]);
  const removePart = (i) => setTemplateParts(prev => prev.filter((_, idx) => idx !== i));
  const updatePart = (i, value) => {
    setTemplateParts(prev => {
      const updated = [...prev];
      updated[i] = { part_id: value };
      return updated;
    });
  };

  const addMaterial = () => setTemplateMaterials(prev => [...prev, { item_id: '' }]);
  const removeMaterial = (i) => setTemplateMaterials(prev => prev.filter((_, idx) => idx !== i));
  const updateMaterial = (i, value) => {
    setTemplateMaterials(prev => {
      const updated = [...prev];
      updated[i] = { item_id: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) { toast.error('Template name is required'); return; }
    if (templateParts.some(p => !p.part_id)) { toast.error('Select a part for all part rows'); return; }
    if (templateMaterials.some(m => !m.item_id)) { toast.error('Select a material for all material rows'); return; }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        mould_id: formData.mould_id ? parseInt(formData.mould_id) : null,
        parts: templateParts.map(p => ({ part_id: parseInt(p.part_id) })),
        materials: templateMaterials.map(m => ({ item_id: parseInt(m.item_id) })),
      };

      if (editingItem) {
        await api.put(`/production-templates/${editingItem.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/production-templates', payload);
        toast.success('Template created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete template "${item.name}"?`)) return;
    try {
      await api.delete(`/production-templates/${item.id}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    }
  };

  const filtered = data.filter(d => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (d.template_no || '').toLowerCase().includes(t) || (d.name || '').toLowerCase().includes(t);
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Layers className="w-6 h-6 text-sky-500" /> Production Templates
          </h1>
          <p className="text-slate-500 text-sm">{data.length} templates</p>
        </div>
        {canEdit && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        )}
      </div>

      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search template no, name..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No production templates found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Template No</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mould</TableHead>
                    <TableHead>Parts</TableHead>
                    <TableHead>Materials</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono font-bold">{item.template_no}</TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-sm">{getMouldLabel(item.mould_id)}</TableCell>
                      <TableCell className="text-sm">
                        {(item.parts || []).map(p => getPartLabel(p.part_id)).join(', ') || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(item.materials || []).map(m => getItemLabel(m.item_id)).join(', ') || '-'}
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>{item.status}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)} className="h-8 w-8">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} className="h-8 w-8 text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? `Edit ${editingItem.template_no}` : 'New Production Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* Header Fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-4">
              <div className="col-span-2">
                <Label className="form-label">Template Name <span className="text-red-500">*</span></Label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Bumper Mould Run" className="rounded-sm" />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 md:col-span-2">
                <Label className="form-label">Mould</Label>
                <Select value={formData.mould_id} onValueChange={(v) => setFormData(prev => ({ ...prev, mould_id: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select Mould" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {moulds.map(m => (
                      <SelectItem key={m.id} value={m.id.toString()}>{m.code} - {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 md:col-span-3">
                <Label className="form-label">Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes" className="rounded-sm" />
              </div>
            </div>

            {/* Parts Section */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Parts</h3>
                <Button type="button" variant="outline" size="sm" onClick={addPart} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Part
                </Button>
              </div>
              {templateParts.length === 0 ? (
                <p className="text-sm text-slate-400 mb-3">No parts added. Click "Add Part" to add.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {templateParts.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select value={row.part_id} onValueChange={(v) => updatePart(i, v)}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Select Part" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {parts.map(p => (
                              <SelectItem key={p.id} value={p.id.toString()}>{p.part_no} - {p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePart(i)} className="h-9 w-9 text-red-500">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Materials Section */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Materials</h3>
                <Button type="button" variant="outline" size="sm" onClick={addMaterial} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Material
                </Button>
              </div>
              {templateMaterials.length === 0 ? (
                <p className="text-sm text-slate-400 mb-3">No materials added. Click "Add Material" to add.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {templateMaterials.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select value={row.item_id} onValueChange={(v) => updateMaterial(i, v)}>
                          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Select Material" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {items.map(it => (
                              <SelectItem key={it.id} value={it.id.toString()}>{it.code} - {it.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeMaterial(i)} className="h-9 w-9 text-red-500">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductionTemplate;
